import { useCallback, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  toggleZapSetterAtom,
  createNotificationSetter,
  AssetType,
  emitUserInteractionEventSetter,
  capitalizedAssetTypeDisplayAtom,
} from "@zapier/canvas-state";

interface UseAssetToggleProps {
  assetType: AssetType;
  assetId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseAssetToggleReturn {
  toggleAsset: (isEnabled: boolean) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to toggle asset (Zap/Agent) enabled/disabled status
 *
 * Usage example:
 * ```tsx
 * const { toggleAsset, isLoading, error } = useAssetToggle({
 *   assetType: "zap",
 *   assetId: "123",
 *   onSuccess: () => console.log("Asset toggled successfully"),
 *   onError: (error) => console.error("Failed to toggle:", error),
 * });
 *
 * // In your component
 * <ToggleSwitch
 *   checked={isEnabled}
 *   onChange={() => toggleAsset(isEnabled)}
 *   disabled={isLoading}
 * />
 * ```
 */
export function useAssetToggle({
  assetType,
  assetId,
  onSuccess,
  onError,
}: UseAssetToggleProps): UseAssetToggleReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleZap = useSetAtom(toggleZapSetterAtom);
  const createNotification = useSetAtom(createNotificationSetter);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);
  const getAssetDisplayName = useAtomValue(capitalizedAssetTypeDisplayAtom);

  const toggleAsset = useCallback(
    async (isEnabled: boolean) => {
      // Only Zaps are supported for now
      if (assetType !== "zap") {
        // Agents not yet supported, but hook is ready for future implementation
        return;
      }

      setIsLoading(true);
      setError(null);

      const targetState: "enable" | "disable" = isEnabled
        ? "disable"
        : "enable";

      // Track toggle click
      await emitUserInteractionEvent({
        eventAction: "click",
        interactionGoal: `${targetState} ${assetType}`,
        rawText: `Toggle ${assetType} ${targetState}`,
        optionSelected: assetType,
      });

      try {
        // Use the setter atom which accesses zapDetails only when called
        await toggleZap({ zapId: assetId, shouldEnable: !isEnabled });
        onSuccess?.();
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMsg);

        // Show error notification
        createNotification({
          type: "error",
          message: `Failed to ${isEnabled ? "disable" : "enable"} ${getAssetDisplayName(assetType)}. Please edit the ${getAssetDisplayName(assetType)} and correct any errors.`,
        });

        onError?.(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [
      assetType,
      assetId,
      toggleZap,
      createNotification,
      emitUserInteractionEvent,
      getAssetDisplayName,
      onSuccess,
      onError,
    ],
  );

  return {
    toggleAsset,
    isLoading,
    error,
  };
}
